import styled from '@emotion/styled';
import { BucketId, FileReference } from '@kaizenplatform/belfast-entity';
import { Button as _Button, Tooltip } from '@material-ui/core';
import _Edit from '@material-ui/icons/Edit';
import { ChangeEvent, ClassAttributes, createElement, Fragment, MouseEventHandler, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { lifecycle } from 'recompose';

import {
  DailySummaryPerformanceWithAllFieldsFragment,
  PerformanceReport,
  PerformanceReportDetailRequest,
  PerformanceReportDimension,
  PerformanceReportNetwork,
  PerformanceReportSummaryRequest,
  PeriodicDetailPerformanceWithAllFieldsFragment,
  PeriodicImprovementPerformanceWithAllFieldsFragment,
  ProjectDetailsForShowFragment,
} from '../../../generated/graphql';
import { isDemo, ProjectForBrief } from '../../../graphql/project';
import { SCREEN_LARGE } from '../../constant/pageWidth';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';
import _Brief from './Brief';
import _ProjectDeliverables from './Deliverables/ProjectDeliverables';
import _Header from './Header';
import _ProjectProposalList from './ProposalList/ProjectProposalList';
import RemoveNotificationDialog from './RemoveNotificationDialog';
import _Reporting from './Reporting';
import _Tabs, { TabName } from './Tabs';
import { useOffer } from './useOffer';

interface Props extends ClassAttributes<HTMLElement> {
  tabName: TabName;
  project: (ProjectForBrief & ProjectDetailsForShowFragment) | null;
  basicProjectTypeIdByJp: string;
  basicProjectTypeIdByUs: string;
  briefFiles: Map<BucketId, FileReference[]>;
  assetFiles: ReadonlyArray<FileReference>;
  projectLoading: boolean;
  performanceReports: Map<
    string,
    {
      report: PerformanceReport;
      request: PerformanceReportSummaryRequest;
      dailySummaryPerformanceFetching: boolean;
      dailySummaryPerformance: DailySummaryPerformanceWithAllFieldsFragment[] | null;
      periodicDetailPerformanceFetching: boolean;
      periodicDetailPerformance: PeriodicDetailPerformanceWithAllFieldsFragment | null;
      periodicImprovementPerformanceFetching: boolean;
      periodicImprovementPerformance: PeriodicImprovementPerformanceWithAllFieldsFragment | null;
    }
  >;
  hasLocalProjectData: boolean;
  isRemovedLocalData: boolean;
  onReportRequestChange: (report: PerformanceReport, reportRequest: PerformanceReportSummaryRequest) => void;
  onClickEditProject: () => void;
  onSummaryRequestChange: (report: PerformanceReport, reportRequest: PerformanceReportSummaryRequest) => void;
  onRequestExportingPerformanceReport: (
    report: PerformanceReport,
    reportRequest: PerformanceReportDetailRequest,
    dimension: PerformanceReportDimension,
  ) => void;
  onIntegrationBannerClose: (adPlatform: PerformanceReportNetwork) => void;
  isIntegrationBannerVisible: (adPlatform: PerformanceReportNetwork) => boolean;
  onClickReorderButton: MouseEventHandler<HTMLElement>;
  onChangeTab: (e: ChangeEvent<HTMLElement>, tabName: TabName) => void;
  // onGotoAnalyticsPage: () => void;
  onMount: () => void;
  onUnmount: () => void;
  className?: string;
  onCloseRemoveNotificationDialog: () => void;
  onSubmitProjectIssue: (projectId: string, inputComment: string) => Promise<void>;
  onSubmitProjectIssueComment: (issueId: string, comment: string) => void;
  onUpdateProjectIssueComment: (commentId: string, comment: string) => void;
  onDeleteProjectIssueComment: (commentId: string) => void;
  reloadProject: () => void;
}

const ShowProjectPage = lifecycle<Props, Record<string, unknown>>({
  componentDidMount: function () {
    this.props.onMount();
  },
  componentWillUnmount: function () {
    this.props.onUnmount();
  },
})(
  ({
    tabName,
    project,
    basicProjectTypeIdByJp,
    basicProjectTypeIdByUs,
    briefFiles,
    assetFiles,
    projectLoading,
    performanceReports,
    hasLocalProjectData,
    isRemovedLocalData,
    onReportRequestChange,
    onClickEditProject,
    onSummaryRequestChange,
    onClickReorderButton,
    onChangeTab,
    // onGotoAnalyticsPage,
    onRequestExportingPerformanceReport,
    onIntegrationBannerClose,
    isIntegrationBannerVisible,
    className,
    onCloseRemoveNotificationDialog,
    onSubmitProjectIssue,
    onSubmitProjectIssueComment,
    onUpdateProjectIssueComment,
    onDeleteProjectIssueComment,
    reloadProject,
  }: Props) => {
    const { loadOffer, dataOffer, refetchOffer } = useOffer();
    useEffect(() => {
      if (project?.creationSet?.offerId) {
        loadOffer(project?.creationSet?.offerId);
      }
    }, [loadOffer, project?.creationSet?.offerId]);

    let tabContent = null;

    if (tabName === 'proposal') {
      tabContent = (
        <Proposal
          project={project}
          offer={dataOffer?.offer ?? null}
          reload={() => {
            refetchOffer();
            reloadProject();
          }}
        />
      );
    }

    if (tabName === 'deliverables') {
      tabContent = (
        <Deliverables project={project} offer={dataOffer?.offer ?? null} reloadOffer={() => refetchOffer()} />
      );
    }

    if (tabName === 'brief') {
      tabContent = (
        <Brief
          project={project}
          assetFiles={assetFiles}
          briefFiles={briefFiles}
          basicProjectTypeIdByJp={basicProjectTypeIdByJp}
          basicProjectTypeIdByUs={basicProjectTypeIdByUs}
          loading={projectLoading}
          onSubmitProjectIssue={onSubmitProjectIssue}
          onSubmitProjectIssueComment={onSubmitProjectIssueComment}
          onUpdateProjectIssueComment={onUpdateProjectIssueComment}
          onDeleteProjectIssueComment={onDeleteProjectIssueComment}
        />
      );
    }

    if (tabName === 'reporting') {
      tabContent = (
        <Reporting
          project={project}
          performanceReports={performanceReports}
          onReportRequestChange={onReportRequestChange}
          onSummaryRequestChange={onSummaryRequestChange}
          onRequestExporting={onRequestExportingPerformanceReport}
          onIntegrationBannerClose={onIntegrationBannerClose}
          isIntegrationBannerVisible={isIntegrationBannerVisible}
          // onGotoAnalyticsPage={onGotoAnalyticsPage}
          loading={projectLoading}
        />
      );
    }

    const isV1ProjectType = () => {
      return !!project && project.projectType.version <= 1;
    };

    const reorderButton =
      project && !isDemo(project) ? (
        <>
          {project.projectType.published ? (
            <Button
              variant="outlined"
              onClick={onClickReorderButton}
              disabled={!project.projectType.published || isV1ProjectType()}
            >
              <FormattedMessage id="show_project_page.actions.reorder" />
            </Button>
          ) : (
            <FormattedMessage id="show_project_page.actions.reorder_is_unabled">
              {(text: string) => (
                <Tooltip title={text} disableFocusListener placement="top">
                  <DisabledButtonWithTooltipWrap>
                    <Button disabled={!project.projectType.published}>
                      <FormattedMessage id="show_project_page.actions.reorder" />
                    </Button>
                  </DisabledButtonWithTooltipWrap>
                </Tooltip>
              )}
            </FormattedMessage>
          )}

          <Button onClick={onClickEditProject}>
            <Edit />
            {hasLocalProjectData ? (
              <FormattedMessage id="show_project_page.actions.edit_by_local" />
            ) : (
              <FormattedMessage id="show_project_page.actions.edit" />
            )}
          </Button>
        </>
      ) : null;

    return (
      <Root className={className}>
        <Header project={project} />

        {reorderButton && <Actions>{reorderButton}</Actions>}

        <Tabs
          value={tabName}
          onChange={onChangeTab}
          displayProposal={!!project && project.matchingFormat == 'competition'}
          loading={!project || projectLoading}
        />

        {tabContent}

        <RemoveNotificationDialog
          onClickAcceptButton={() => {
            onCloseRemoveNotificationDialog();
          }}
          open={isRemovedLocalData}
          onRequestClose={() => {
            onCloseRemoveNotificationDialog();
          }}
        />
      </Root>
    );
  },
);

const Root = styled('div')(
  gridLayoutContainer({
    columns: [`calc(100% - 480px)`, '480px'],
    rows: ['auto', 'auto', 'auto'],
    areas: [
      ['header', 'header'],
      ['tabs', 'actions'],
      ['tabContent', 'tabContent'],
    ],
  }),
  {
    width: 'auto',
    maxWidth: SCREEN_LARGE,
    marginTop: SPACING_HUGE,
    marginRight: 'auto',
    paddingBottom: SPACING_UNIT * 12,
    marginLeft: 'auto',
    [mq('sp')]: {
      ...gridLayoutContainer({
        columns: ['1fr'],
        rows: ['auto', 'auto', 'auto', 'auto'],
        areas: [['header'], ['actions'], ['tabs'], ['tabContent']],
      }),
      paddingLeft: SPACING_REGULAR,
      paddingRight: SPACING_REGULAR,
    },
  },
);

const Edit = styled(_Edit)({
  height: '20px',
  marginRight: 4,
});
const Header = styled(_Header)(gridLayoutItem('header'), { marginBottom: SPACING_REGULAR });

const Tabs = styled(_Tabs)(gridLayoutItem('tabs'), {
  marginBottom: SPACING_HUGE,
  [mq('sp')]: {
    marginBottom: SPACING_UNIT * 3,
  },
});

const Actions = styled('div')({
  ...gridLayoutItem('actions', { justifySelf: 'auto' }),
  ...flexibleLayoutContainer({
    direction: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  }),
  marginBottom: SPACING_UNIT * 5,
  [mq('sp')]: {
    marginBottom: SPACING_UNIT * 3,
  },
});

const Deliverables = styled(_ProjectDeliverables)(gridLayoutItem('tabContent'));

const Proposal = styled(_ProjectProposalList)(gridLayoutItem('tabContent'));

const Brief = styled(_Brief)(gridLayoutItem('tabContent'));

const Reporting = styled(_Reporting)(gridLayoutItem('tabContent'));

const DisabledButtonWithTooltipWrap = styled('div')({});

const Button = styled(_Button)({
  paddingLeft: 12,
  paddingRight: 12,
  marginLeft: 10,
});

export default ShowProjectPage;
